import Alpine from "alpinejs";
import { naturalCompare } from "../utils/string";

let callback = null;

Alpine.data("recipientPicker", () => ({
  recipients: [],
  invalidRecipients: [],
  isSigning: false,
  entity: null,
  selectedCount: 0,
  globalMode: "trainees",

  init() {
    this.titles = JSON.parse(this.$root.dataset.titles);
    this.labels = JSON.parse(this.$root.dataset.labels);

    const refresh = () => {
      if (!this.$refs.selectAllCheckbox) return;
      const visible = this.recipients.filter((r) => this.matchesGlobalMode(r));
      this.selectedCount = visible.filter((recipient) => recipient.checked).length;
      if (this.selectedCount === 0) {
        this.$refs.selectAllCheckbox.indeterminate = false;
        this.$refs.selectAllCheckbox.checked = false;
      } else if (this.selectedCount === visible.length) {
        this.$refs.selectAllCheckbox.indeterminate = false;
        this.$refs.selectAllCheckbox.checked = true;
      } else {
        this.$refs.selectAllCheckbox.indeterminate = true;
        this.$refs.selectAllCheckbox.checked = false;
      }
    };
    this.$watch("recipients", refresh);
    this.$watch("globalMode", refresh);
  },
  pick({ recipients, invalidRecipients, isSigning, entity }) {
    this.recipients = recipients
      .map((recipient, idx) => {
        // We can have SignerInfo instead of EmailRecipient, so we need to do some adjustments
        if (!recipient.name) {
          recipient.name = `${recipient.lastname} ${recipient.firstname || ""}`;
        }
        recipient.tags = recipient.tags || [];
        return { ...recipient, idx };
      })
      .sort((a, b) => naturalCompare(a.name, b.name));
    this.invalidRecipients = invalidRecipients || [];
    this.isSigning = !!isSigning;
    this.entity = entity || null;
    // Reset the active tab deterministically on every open (the picker is a shared
    // singleton, so globalMode would otherwise persist from a previous open and, with
    // onPicked now scoped to the active tab, could leave the picker on an empty tab).
    // Default to trainees, falling back to contacts when there are no trainees.
    this.globalMode = this.anyTraineeAvailable ? "trainees" : "contacts";

    this.$popup.open(this.$root);

    return new Promise((resolve) => (callback = resolve));
  },
  // Tells whether a recipient belongs to the given mode (contacts or trainees)
  // Defaults to the current globalMode.
  matchesGlobalMode(recipient, mode = this.globalMode) {
    const isCompanyContact = recipient.type === "company";

    return mode === "contacts" ? isCompanyContact : !isCompanyContact;
  },
  setSelection(newValue) {
    this.recipients.forEach((recipient) => {
      if (this.matchesGlobalMode(recipient)) {
        recipient.checked = newValue;
      }
    });
  },
  setGroupSelection(group, newValue) {
    group.recipients.forEach((recipient) => {
      recipient.checked = newValue;
    });
  },
  setGlobalMode(mode) {
    this.recipients.forEach((recipient) => {
      if (!this.matchesGlobalMode(recipient, mode)) {
        recipient.checked = false;
      }
    });
    this.globalMode = mode;
  },
  hidePicker() {
    this.$popup.close(this.$root);
    callback?.(null);
  },
  onPicked() {
    const picked = this.recipients.filter((recipient) => recipient.checked && this.matchesGlobalMode(recipient));
    if (picked.length > 0) {
      this.$popup.close(this.$root);
      callback?.(picked);
    } else {
      console.error("Nobody picked");
    }
  },
  allRecipientsHavePhoneNumber() {
    return this.recipients.every((recipient) => recipient.phone);
  },
  editLink(entity) {
    switch (entity?.type) {
      case "company":
        return p`/companies/${entity.id}/edit`;
      case "trainee":
        return p`/trainees/${entity.id}/edit`;
      case "instructor":
        return p`/instructors/${entity.id}/edit`;
      case "funding_agency":
        return p`/funding_agencies/${entity.context.funding_agency_id}/edit`;
      case "funding_agency_contact":
        return p`/funding_agencies/${entity.context.funding_agency_id}/edit?tab=contacts`;
      default:
        return p`/academy/edit`;
    }
  },
  get groupedRecipients() {
    // Only group when at least one recipient has a company_id
    const hasGroups = this.recipients.some((r) => r.company_id);
    if (!hasGroups) return null;

    const visible = this.recipients.filter((r) => this.matchesGlobalMode(r));

    const companyGroups = {};
    const ungrouped = [];

    for (const recipient of visible) {
      if (recipient.company_id) {
        if (!companyGroups[recipient.company_id]) {
          companyGroups[recipient.company_id] = { name: recipient.company_name, recipients: [] };
        }
        companyGroups[recipient.company_id].recipients.push(recipient);
      } else {
        ungrouped.push(recipient);
      }
    }

    // Sort company groups alphabetically by name
    const sorted = Object.values(companyGroups)
      .sort((a, b) => naturalCompare(a.name, b.name))
      .map(({ name, recipients }, idx) => ({
        key: `company_${idx}`,
        name,
        recipients,
        isCompany: true,
        allSelected: recipients.length > 0 && recipients.every((r) => r.checked),
      }));

    // Append ungrouped (individual/independent) recipients at the end
    if (ungrouped.length > 0) {
      sorted.push({
        key: "__ungrouped__",
        name: null,
        recipients: ungrouped,
        isCompany: false,
        allSelected: ungrouped.every((r) => r.checked),
      });
    }

    return sorted;
  },
  get anyCompanyContactAvailable() {
    return this.recipients.some((r) => r.type === "company" && r.company_id);
  },
  get anyTraineeAvailable() {
    return this.recipients.some((r) => r.type !== "company");
  },
}));

export function pick(opts) {
  return Alpine.$data(document.querySelector("#recipient-picker")).pick(opts);
}
